import { decodeJwt } from 'jose'
import type { TokenPayload, SessionStatus } from '@/types/session'

/** What a join link can carry, whichever channel it arrived on. */
export interface UrlCredentials {
  token: string | null
  /** The patient's display name, which only ever travels in the fragment. */
  patientName: string | null
}

/**
 * Read the session token out of the current URL.
 *
 * The fragment is preferred and is what the portal emits. Browsers never send a
 * fragment to a server, so a token delivered that way stays out of our access
 * logs, and the patient's display name - which the portal appends as `pn` -
 * reaches this frontend without touching any backend.
 *
 * The query string is still read because that is how a patient's mailed link
 * arrives: fragments do not survive mail clients and link rewriters reliably
 * enough to bet a join link on.
 *
 * This is the single place that knows which channels carry a token. Anything
 * asking "is there a token in the URL?" must go through here - the router guard
 * once answered that from `location.search` alone, which made every
 * fragment-delivered link (i.e. the portal's own) look tokenless.
 */
export function readCredentialsFromUrl(
  source: Pick<Location, 'hash' | 'search'> = window.location,
): UrlCredentials {
  const fragment = new URLSearchParams(source.hash.replace(/^#/, ''))
  const query = new URLSearchParams(source.search)

  return {
    token: fragment.get('token') ?? query.get('token'),
    patientName: fragment.get('pn'),
  }
}

export function decodeTokenLocally(raw: string): TokenPayload | null {
  try {
    const payload = decodeJwt(raw) as unknown as TokenPayload
    if (!payload.sub) {
      return null
    }
    // Template-editor tokens only need sub and therapist_id
    if (payload.purpose === 'template-editor') {
      return payload.therapist_id ? payload : null
    }
    // Session tokens need role and time fields
    if (!payload.role || !payload.time_start || !payload.time_end) {
      return null
    }
    return payload
  } catch {
    return null
  }
}

export function validateTokenTime(payload: TokenPayload): SessionStatus {
  const now = Math.floor(Date.now() / 1000)

  // Template-editor tokens only use nbf/exp, no time_start/time_end
  if (payload.purpose === 'template-editor') {
    if (now < payload.nbf) return 'too_early'
    if (now > payload.exp) return 'expired'
    return 'valid'
  }

  if (now < payload.nbf) {
    return 'too_early'
  }

  if (now > payload.exp) {
    return 'expired'
  }

  return 'valid'
}

export function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}
